import type { OperatorSettingsSectionId } from "./operatorWorkspace.types";

export type OperatorWorkspaceView =
  | "overview"
  | "connections"
  | "repositories"
  | "create"
  | "loops"
  | "settings";
export type MainWorkspaceView = Exclude<OperatorWorkspaceView, "create" | "settings">;

export type OperatorWorkspaceRouteSearch = {
  readonly view?: OperatorWorkspaceView;
  readonly settings?: OperatorSettingsSectionId;
  readonly automation?: string;
  readonly loop?: string;
  readonly returnView?: MainWorkspaceView;
  readonly environment?: string;
  readonly project?: string;
  readonly slackLink?: string;
  readonly integrationConnect?: string;
  readonly mcpConsent?: string;
  readonly repository?: string;
  readonly repositoryConnection?: string;
};

const workspaceViews = new Set<OperatorWorkspaceView>([
  "overview",
  "connections",
  "repositories",
  "create",
  "loops",
  "settings",
]);
const mainWorkspaceViews = new Set<MainWorkspaceView>([
  "overview",
  "connections",
  "repositories",
  "loops",
]);
const settingsSections = new Set<OperatorSettingsSectionId>([
  "credits",
  "profile",
  "workspace",
  "environments",
  "knowledge",
  "skills",
  "members",
  "providers",
  "coding-provider",
  "connections",
  "repositories",
  "slack",
  "email",
  "logs",
  "vault",
  "mcp",
  "workspace-mcp",
]);

function optionalString(value: unknown) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

export function validateOperatorWorkspaceSearch(
  search: Record<string, unknown>,
): OperatorWorkspaceRouteSearch {
  const legacyLoopsRoute = search.view === "settings" && search.settings === "automations";
  const legacyConnectionsRoute =
    search.view === "settings" && (search.settings === "connections" || search.settings === "mcp");
  const repositorySettingsRoute = search.view === "settings" && search.settings === "repositories";
  const legacySkillsRoute = search.view === "skills";
  const view = repositorySettingsRoute
    ? "repositories"
    : legacySkillsRoute
      ? "settings"
      : legacyConnectionsRoute
        ? "connections"
        : legacyLoopsRoute
          ? "loops"
          : workspaceViews.has(search.view as OperatorWorkspaceView)
            ? (search.view as OperatorWorkspaceView)
            : undefined;
  const settings =
    repositorySettingsRoute || (legacyConnectionsRoute && search.settings === "connections")
      ? undefined
      : legacySkillsRoute
        ? "skills"
        : search.settings === "coding-provider"
          ? "providers"
          : settingsSections.has(search.settings as OperatorSettingsSectionId)
            ? (search.settings as OperatorSettingsSectionId)
            : undefined;
  const automation = view === "loops" ? optionalString(search.automation) : undefined;
  const loop = view === "loops" ? optionalString(search.loop) : undefined;
  const returnView = mainWorkspaceViews.has(search.returnView as MainWorkspaceView)
    ? (search.returnView as MainWorkspaceView)
    : undefined;
  const environment = optionalString(search.environment);
  const project = optionalString(search.project);
  const slackLink = optionalString(search.slackLink);
  const integrationConnect = optionalString(search.integrationConnect);
  const mcpConsent = optionalString(search.mcpConsent);
  const repository = optionalString(search.repository);
  const repositoryConnection = optionalString(search.repositoryConnection);

  return {
    ...(view === undefined ? {} : { view }),
    ...(settings === undefined || settings === "workspace" ? {} : { settings }),
    ...(automation === undefined ? {} : { automation }),
    ...(loop === undefined ? {} : { loop }),
    ...(returnView === undefined ? {} : { returnView }),
    ...(environment === undefined ? {} : { environment }),
    ...(project === undefined ? {} : { project }),
    ...(slackLink === undefined ? {} : { slackLink }),
    ...(integrationConnect === undefined ? {} : { integrationConnect }),
    ...(mcpConsent === undefined ? {} : { mcpConsent }),
    ...(repository && repositoryConnection ? { repository, repositoryConnection } : {}),
  };
}

export function workspaceViewFromSearch(search: OperatorWorkspaceRouteSearch) {
  return workspaceViews.has(search.view as OperatorWorkspaceView)
    ? search.view!
    : search.settings === undefined
      ? "overview"
      : "settings";
}

// Internal view names remain compatible with existing page components and links.
const workspacePages = [
  { path: "/overview", view: "overview" },
  { path: "/general", view: "settings", settings: "workspace" },
  { path: "/profile", view: "settings", settings: "profile" },
  { path: "/members", view: "settings", settings: "members" },
  { path: "/environments", view: "settings", settings: "environments" },
  { path: "/knowledge", view: "settings", settings: "knowledge" },
  { path: "/skills", view: "settings", settings: "skills" },
  { path: "/providers", view: "settings", settings: "providers" },
  { path: "/usage", view: "settings", settings: "credits" },
  { path: "/email", view: "settings", settings: "email" },
  { path: "/logs", view: "settings", settings: "logs" },
  { path: "/slack", view: "settings", settings: "slack" },
  { path: "/vault", view: "settings", settings: "vault" },
  { path: "/mcp", view: "settings", settings: "workspace-mcp" },
  { path: "/connections", view: "connections" },
  { path: "/connections/mcp", view: "connections", settings: "mcp" },
  { path: "/repositories", view: "repositories" },
  { path: "/loops", view: "loops" },
  { path: "/create", view: "create" },
] as const satisfies ReadonlyArray<{
  readonly path: string;
  readonly view: OperatorWorkspaceView;
  readonly settings?: OperatorSettingsSectionId;
}>;

export const workspacePagePaths = workspacePages.map((page) => page.path);

function workspacePageFromPath(pathname: string) {
  const path = pathname.replace(/\/$/, "");
  return workspacePages.find((page) => page.path === path);
}

export function workspaceSectionFromPath(pathname: string) {
  const page = workspacePageFromPath(pathname);
  if (page !== undefined) return page.view;
  return /^\/(chat|home|activity|archived)(?:\/[^/]+)?\/?$/.test(pathname) ? "overview" : null;
}

export function operatorWorkspaceSearchFromLocation(location: {
  readonly pathname: string;
  readonly search: Record<string, unknown>;
}): OperatorWorkspaceRouteSearch {
  const search = validateOperatorWorkspaceSearch(location.search);
  const page = workspacePageFromPath(location.pathname);
  if (page !== undefined) {
    const next = omitWorkspaceSearch(search, "view", "settings");
    return {
      ...next,
      view: page.view,
      ...("settings" in page
        ? { settings: page.settings }
        : page.path === "/connections" && search.settings === "mcp"
          ? { settings: "mcp" as const }
          : {}),
    };
  }
  const section = workspaceSectionFromPath(location.pathname);
  return section === null ? search : { ...omitWorkspaceSearch(search, "settings"), view: section };
}

export function operatorWorkspaceLocation(
  search: OperatorWorkspaceRouteSearch,
  taskId?: string,
  implicitWorkspace = false,
) {
  // Authorization and account-link requests carry an explicit destination that
  // may belong to another organization. Keep it through sign-in and consent.
  if (search.mcpConsent || search.integrationConnect || search.slackLink) {
    return { to: "/" as const, search };
  }
  const view = workspaceViewFromSearch(search);
  let next = omitWorkspaceSearch(search, "view", "settings");
  if (implicitWorkspace) {
    next = omitWorkspaceSearch(next, "environment", "project");
    next = omitWorkspaceSearch(next, "returnView");
  }
  const selectedSection = settingsSectionFromSearch(search);
  const section = selectedSection === "coding-provider" ? "providers" : selectedSection;
  const page = workspacePages.find(
    (page) =>
      page.view === view &&
      ("settings" in page
        ? page.settings === section
        : view !== "connections" || section === "connections"),
  );
  return { to: page?.path ?? "/overview", search: next };
}

export function settingsSectionFromSearch(search: OperatorWorkspaceRouteSearch) {
  if (workspaceViewFromSearch(search) === "repositories") return "repositories";
  return (
    search.settings ??
    (workspaceViewFromSearch(search) === "connections"
      ? "connections"
      : search.project === undefined
        ? "providers"
        : "workspace")
  );
}

export function omitWorkspaceSearch(
  search: OperatorWorkspaceRouteSearch,
  ...keys: ReadonlyArray<keyof OperatorWorkspaceRouteSearch>
) {
  const next = { ...search };
  for (const key of keys) delete next[key];
  return next;
}

export function createWorkspaceSearch(search: OperatorWorkspaceRouteSearch) {
  return {
    ...omitWorkspaceSearch(search, "environment", "project"),
    view: "create" as const,
  };
}

export function scopeWorkspaceEntitySearch(
  search: OperatorWorkspaceRouteSearch,
  environmentId: string | null,
) {
  const next = omitWorkspaceSearch(search, "environment");
  const hasEntitySelection = next.project !== undefined || next.repository !== undefined;

  return environmentId === null || !hasEntitySelection
    ? next
    : { ...next, environment: environmentId };
}

export function selectProjectSearch(
  search: OperatorWorkspaceRouteSearch,
  projectId: string,
  environmentId: string | null,
) {
  const next = omitWorkspaceSearch(search, "automation", "loop", "environment", "project", "view");

  return scopeWorkspaceEntitySearch(
    {
      ...next,
      ...(search.view === "overview" ||
      search.view === "settings" ||
      search.view === "connections" ||
      search.view === "repositories"
        ? { view: search.view }
        : {}),
      ...(projectId.length === 0 ? {} : { project: projectId }),
    },
    environmentId,
  );
}

export function onboardProjectSearch(
  search: OperatorWorkspaceRouteSearch,
  projectId: string,
  environmentId: string | null,
) {
  return scopeWorkspaceEntitySearch(
    {
      ...omitWorkspaceSearch(search, "environment"),
      view: "create",
      project: projectId,
    },
    environmentId,
  );
}

export function reconcileWorkspaceSearch(
  search: OperatorWorkspaceRouteSearch,
  { projectValid }: { readonly projectValid: boolean },
  environmentId: string | null,
) {
  const next = projectValid ? search : omitWorkspaceSearch(search, "automation", "loop", "project");
  return scopeWorkspaceEntitySearch(next, environmentId);
}

export function openSettingsSearch(
  search: OperatorWorkspaceRouteSearch,
  section?: OperatorSettingsSectionId,
): OperatorWorkspaceRouteSearch {
  const currentView = workspaceViewFromSearch(search);
  const returnView =
    currentView === "settings" || currentView === "create"
      ? (search.returnView ?? "overview")
      : currentView;

  return {
    ...omitWorkspaceSearch(
      search,
      "automation",
      "loop",
      "settings",
      "repository",
      "repositoryConnection",
    ),
    view: "settings",
    returnView,
    ...(section === undefined || section === "workspace" ? {} : { settings: section }),
  };
}

export function closeSettingsSearch(
  search: OperatorWorkspaceRouteSearch,
): OperatorWorkspaceRouteSearch {
  const returnView = mainWorkspaceViews.has(search.returnView as MainWorkspaceView)
    ? (search.returnView as MainWorkspaceView)
    : "overview";

  const next = omitWorkspaceSearch(search, "automation", "loop", "view", "settings", "returnView");
  return { ...next, view: returnView };
}

export function openAutomationEditorSearch(
  search: OperatorWorkspaceRouteSearch,
  automationId?: string,
): OperatorWorkspaceRouteSearch {
  const next = omitWorkspaceSearch(search, "loop", "returnView", "settings");
  return {
    ...next,
    view: "loops",
    automation: automationId ?? "new",
  };
}

export function closeAutomationEditorSearch(
  search: OperatorWorkspaceRouteSearch,
): OperatorWorkspaceRouteSearch {
  return omitWorkspaceSearch(search, "automation");
}

export function openLoopSearch(
  search: OperatorWorkspaceRouteSearch,
  loopId: string,
): OperatorWorkspaceRouteSearch {
  return {
    ...omitWorkspaceSearch(search, "automation", "returnView", "settings"),
    view: "loops",
    loop: loopId,
  };
}

export function closeLoopSearch(
  search: OperatorWorkspaceRouteSearch,
): OperatorWorkspaceRouteSearch {
  return omitWorkspaceSearch(search, "automation", "loop");
}
